import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Loader2, Gauge, Clock, Zap, Database, HardDrive, Server, Shield,
  AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw, Activity, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import adminAxios from './adminAxios';

const AdminMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [period, setPeriod] = useState(60);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await adminAxios.get(`${API}/admin/metrics?minutes=${period}`);
      setMetrics(res.data);
    } catch (e) {
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchMetrics();
    if (!autoRefresh) return;
    const interval = setInterval(fetchMetrics, 10000);
    return () => clearInterval(interval);
  }, [fetchMetrics, autoRefresh]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!metrics) return <div className="text-center py-20 text-slate-500">Sem dados de métricas</div>;

  const getStatusColor = (val, thresholds) => {
    if (val <= thresholds[0]) return 'text-emerald-600';
    if (val <= thresholds[1]) return 'text-amber-500';
    return 'text-red-500';
  };

  const getStatusBg = (val, thresholds) => {
    if (val <= thresholds[0]) return 'bg-emerald-50 border-emerald-200';
    if (val <= thresholds[1]) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6" data-testid="admin-metrics">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Gauge className="w-6 h-6 text-blue-600" />Performance em Tempo Real
          </h2>
          <p className="text-sm text-slate-500 mt-1">Monitoramento de APIs, banco de dados e cache</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={period} onChange={e => setPeriod(Number(e.target.value))} className="text-sm border rounded-lg px-3 py-1.5 bg-white" data-testid="metrics-period-select">
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 hora</option>
            <option value={360}>6 horas</option>
          </select>
          <button onClick={() => setAutoRefresh(!autoRefresh)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${autoRefresh ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            data-testid="metrics-auto-refresh-btn">
            {autoRefresh ? <><Zap className="w-3 h-3 inline mr-1" />Auto</> : 'Pausado'}
          </button>
          <Button size="sm" variant="outline" onClick={fetchMetrics} data-testid="metrics-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`border ${getStatusBg(metrics.avg_response_ms, [200, 500])}`} data-testid="metric-avg-response">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Clock className={`w-5 h-5 ${getStatusColor(metrics.avg_response_ms, [200, 500])}`} />
              {metrics.avg_response_ms <= 200 ? <ArrowDownRight className="w-4 h-4 text-emerald-500" /> : <ArrowUpRight className="w-4 h-4 text-red-500" />}
            </div>
            <p className={`text-2xl font-bold mt-2 ${getStatusColor(metrics.avg_response_ms, [200, 500])}`}>
              {metrics.avg_response_ms}ms
            </p>
            <p className="text-xs text-slate-500 mt-1">Tempo Médio de Resposta</p>
          </CardContent>
        </Card>

        <Card className={`border ${getStatusBg(metrics.p95_response_ms, [500, 1000])}`} data-testid="metric-p95-response">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Zap className={`w-5 h-5 ${getStatusColor(metrics.p95_response_ms, [500, 1000])}`} />
              <span className="text-xs font-mono bg-slate-200 px-1.5 py-0.5 rounded">P95</span>
            </div>
            <p className={`text-2xl font-bold mt-2 ${getStatusColor(metrics.p95_response_ms, [500, 1000])}`}>
              {metrics.p95_response_ms}ms
            </p>
            <p className="text-xs text-slate-500 mt-1">Latência P95</p>
          </CardContent>
        </Card>

        <Card className={`border ${getStatusBg(metrics.overall_error_rate, [1, 5])}`} data-testid="metric-error-rate">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <AlertTriangle className={`w-5 h-5 ${getStatusColor(metrics.overall_error_rate, [1, 5])}`} />
              {metrics.overall_error_rate <= 1 ? <ArrowDownRight className="w-4 h-4 text-emerald-500" /> : <ArrowUpRight className="w-4 h-4 text-red-500" />}
            </div>
            <p className={`text-2xl font-bold mt-2 ${getStatusColor(metrics.overall_error_rate, [1, 5])}`}>
              {metrics.overall_error_rate}%
            </p>
            <p className="text-xs text-slate-500 mt-1">Taxa de Erros</p>
          </CardContent>
        </Card>

        <Card className={`border ${getStatusBg(100 - metrics.cache_hit_rate, [40, 70])}`} data-testid="metric-cache-hit">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Database className={`w-5 h-5 ${metrics.cache_hit_rate >= 60 ? 'text-emerald-600' : 'text-amber-500'}`} />
              <span className="text-xs font-mono bg-slate-200 px-1.5 py-0.5 rounded">Cache</span>
            </div>
            <p className={`text-2xl font-bold mt-2 ${metrics.cache_hit_rate >= 60 ? 'text-emerald-600' : 'text-amber-500'}`}>
              {metrics.cache_hit_rate}%
            </p>
            <p className="text-xs text-slate-500 mt-1">Taxa de Cache Hit ({metrics.cache_hits}h / {metrics.cache_misses}m)</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="metric-timeline-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />Requests por Minuto
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={metrics.timeline} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="requests" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Requests" />
                  <Bar dataKey="errors" fill="#ef4444" radius={[3, 3, 0, 0]} name="Erros" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">Aguardando dados...</div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="metric-latency-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />Latência por Minuto (ms)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={metrics.timeline} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="avg_ms" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Média (ms)" />
                  <Bar dataKey="p95_ms" fill="#ef4444" radius={[3, 3, 0, 0]} name="P95 (ms)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">Aguardando dados...</div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="metric-slowest-endpoints">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-500" />Endpoints Mais Lentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {metrics.slowest_endpoints.slice(0, 10).map((ep, i) => (
                <div key={ep.endpoint || i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`slowest-endpoint-${i}`}>
                  <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center ${i < 3 ? 'bg-red-100 text-red-600' : 'bg-slate-200 text-slate-500'}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-700 truncate">{ep.endpoint}</p>
                    <div className="flex gap-3 mt-0.5">
                      <span className="text-[10px] text-slate-400">{ep.count} reqs</span>
                      <span className={`text-[10px] font-semibold ${ep.avg_ms > 500 ? 'text-red-500' : ep.avg_ms > 200 ? 'text-amber-500' : 'text-emerald-500'}`}>
                        avg {ep.avg_ms}ms
                      </span>
                      <span className="text-[10px] text-slate-400">max {ep.max_ms}ms</span>
                      {ep.error_rate > 0 && <span className="text-[10px] text-red-500">{ep.error_rate}% err</span>}
                    </div>
                  </div>
                  <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${ep.avg_ms > 500 ? 'bg-red-500' : ep.avg_ms > 200 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min((ep.avg_ms / Math.max(...metrics.slowest_endpoints.map(e => e.avg_ms))) * 100, 100)}%` }} />
                  </div>
                </div>
              ))}
              {metrics.slowest_endpoints.length === 0 && (
                <p className="text-center text-slate-400 text-sm py-8">Nenhum endpoint registrado ainda</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {metrics.db_stats && (
            <Card data-testid="metric-db-stats">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-purple-500" />Banco de Dados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-purple-50 rounded-lg">
                    <p className="text-lg font-bold text-purple-700">{metrics.db_stats.total_documents.toLocaleString()}</p>
                    <p className="text-[10px] text-purple-500">Documentos</p>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <p className="text-lg font-bold text-blue-700">{metrics.db_stats.indexes}</p>
                    <p className="text-[10px] text-blue-500">Indices</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded-lg">
                    <p className="text-lg font-bold text-emerald-700">{metrics.db_stats.data_size_mb}MB</p>
                    <p className="text-[10px] text-emerald-500">Dados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {metrics.collection_stats && metrics.collection_stats.length > 0 && (
            <Card data-testid="metric-collection-stats">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Server className="w-4 h-4 text-blue-500" />Collections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {metrics.collection_stats.map((col, i) => (
                    <div key={col.name || i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                      <span className="text-xs font-mono text-slate-600">{col.name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min((col.documents / Math.max(...metrics.collection_stats.map(c => c.documents))) * 100, 100)}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-700 w-12 text-right">{col.documents.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card data-testid="metric-status-dist">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500" />Status HTTP
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(metrics.status_distribution).sort(([a], [b]) => a.localeCompare(b)).map(([code, count]) => (
                  <div key={code} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    code.startsWith('2') ? 'bg-emerald-100 text-emerald-700' :
                    code.startsWith('3') ? 'bg-blue-100 text-blue-700' :
                    code.startsWith('4') ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {code}: {count.toLocaleString()}
                  </div>
                ))}
                {Object.keys(metrics.status_distribution).length === 0 && (
                  <p className="text-sm text-slate-400">Sem dados ainda</p>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-100 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-700">{metrics.total_requests.toLocaleString()}</p>
              <p className="text-[10px] text-slate-500">Total de Requests</p>
            </div>
            <div className="p-3 bg-slate-100 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-700">{metrics.active_endpoints}</p>
              <p className="text-[10px] text-slate-500">Endpoints Ativos</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;
